import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
export function WhereAIFits() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-780">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-781">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-782">
          <BlueprintGrid data-id="element-783" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-784">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-785">
            Where AI Fits
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-786">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-787">
              AI is the answer.
            </span>{' '}
            <span className="text-navy" data-id="element-788">
              But not to the question most people are asking.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mb-8 space-y-4" data-id="element-789">
            <p data-id="element-790">AI can absolutely create leverage.</p>
            <p data-id="element-791">
              It can improve speed, consistency, signal quality,
              troubleshooting, decision support, and bottom-line performance.
            </p>
            <p data-id="element-792">
              But it does not fix fuzzy rules, weak handoffs, scattered
              knowledge, or workflows nobody can define cleanly.
            </p>
            <p data-id="element-793">
              If the business logic is unclear, AI usually accelerates noise.
            </p>
            <p className="font-medium text-navy" data-id="element-794">
              If the workflow is sound, AI can become a real lever.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col" data-id="element-795">
            <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 w-fit" data-id="element-796">
              <span data-id="element-797">Book an Honest Conversation</span>
              <ArrowRight className="w-5 h-5" data-id="element-798" />
            </Link>
            <p className="text-sm text-text-muted mt-2" data-id="element-799">
              45 minutes. No pitch. Just clarity.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* AI works best when attached to something real */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-800">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-801">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-802">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-803">
              AI works best when it is attached to something real
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-804">
            <p className="text-text" data-id="element-805">
              Most AI efforts do not stall because the model is weak.
            </p>
            <p className="text-text" data-id="element-806">
              They stall because the business is not clear enough yet.
            </p>
            <p className="text-text" data-id="element-807">
              The logic is still trapped in people. Definitions drift. Ownership
              is fuzzy. Exceptions are doing the real work. The team cannot
              explain what good looks like clearly enough for the system to
              support it.
            </p>
            <p className="text-text" data-id="element-808">
              That is why AI so often creates the appearance of progress without
              delivering operating leverage.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-809">
              Technology follows clarity. We do not automate confusion.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What AI-ready actually means */}
      <section id="ai-ready" className="relative py-24 px-6 bg-[#8BA89F26] grain border-y border-seaglass/20 overflow-hidden" data-id="element-810">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-811" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-812">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-813">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-814">AI-ready</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-815">
              What AI-ready actually means
            </h2>
            <div className="text-lg text-text space-y-4" data-id="element-816">
              <p data-id="element-817">
                AI-ready does not mean the company is modern, technical, or full
                of tools.
              </p>
              <p data-id="element-818">
                It means the workflow is clear enough for AI to support
                something real.
              </p>
              <p data-id="element-819">That usually requires:</p>
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="mb-10" data-id="element-820">
            <ul className="space-y-3 pl-1" data-id="element-821">
              {['clear definitions', 'clear decision rules', 'clear ownership', 'usable knowledge outside people\u2019s heads', 'clear guardrails around where AI helps and where humans still decide'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-822">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-823" />
                  <span className="text-body text-text" data-id="element-824">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-825">
            <div className="border-l-2 border-seaglass pl-8 py-2 space-y-2" data-id="element-826">
              <p className="text-body text-text font-medium" data-id="element-827">
                If those are missing, the answer is usually not more AI.
              </p>
              <p className="text-body text-text font-medium" data-id="element-828">
                It is better structure first.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* How I use AI in this work */}
      <section className="py-24 px-6 bg-paper" data-id="element-829">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-830">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-831">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-832">
              How I use AI in this work
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-833">
            <p className="text-body text-text" data-id="element-834">
              I use AI to help find leverage and improve bottom-line
              performance.
            </p>

            <p className="text-body text-text" data-id="element-835">That can include:</p>

            <ul className="space-y-3" data-id="element-836">
              {['clarifying decision rules before automation', 'tightening handoffs so outputs do not die in the middle', 'cleaning up definitions so systems produce usable signals', 'redesigning exception paths that currently depend on judgment trapped in people', 'identifying where AI can support speed, consistency, routing, drafting, summarizing, troubleshooting, or signal detection without increasing confusion'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-837">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-838" />
                  <span className="text-body text-text" data-id="element-839">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-navy font-medium" data-id="element-840">
              The point is not to force AI into the business.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-841">
              The point is to make sure it is accelerating something real.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* A simple example */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-842">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-843">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-844">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-845">
              A simple example
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-846">
            <p className="text-body text-text" data-id="element-847">
              A founder had his team build a service ticket platform to improve
              the customer experience.
            </p>

            <p className="text-body text-text" data-id="element-848">
              The interface was fine. But the real leverage was not the UI.
            </p>

            <p className="text-body text-navy font-semibold" data-id="element-849">
              It was the clearer workflow truth underneath it.
            </p>

            <p className="text-body text-text" data-id="element-850">
              Once service data and account data were lined up properly, the
              business could finally see which requests came back inside 30 days
              of onboarding, where installation quality was breaking down
              upstream, and which industries were consuming the most service
              time relative to the revenue they produced.
            </p>

            <p className="text-body text-text" data-id="element-851">
              That created signals the business could actually trust.
            </p>

            <div className="py-8 my-4 border-y hairline-top hairline-bottom space-y-4" data-id="element-852">
              <p className="text-body text-text" data-id="element-853">
                Only after that did AI become useful in the next layer.
              </p>
              <p className="text-body text-text" data-id="element-854">
                With cleaner workflow truth in place, we built a custom GPT for
                field service agents using equipment manuals as reference
                material so they could troubleshoot faster from their phones.
              </p>
            </div>

            <p className="text-body text-navy font-semibold" data-id="element-855">
              That is where AI actually earns its role.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What not to do */}
      <section className="py-24 px-6 bg-paper" data-id="element-856">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-857">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-858">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-859">
              What not to do
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-860">
            <ul className="space-y-3" data-id="element-861">
              {['Do not ask AI to fix a business that still runs on unclear rules.', 'Do not automate a workflow nobody can define cleanly.', 'Do not mistake tool activity for operating leverage.', 'Do not hand a model the job of interpreting what the business itself has not clarified yet.'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-862">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-863" />
                  <span className="text-text" data-id="element-864">{item}</span>
                </li>)}
            </ul>

            <p className="text-text font-medium text-navy" data-id="element-865">
              Fix the recurring failure first.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-866">
              Then decide what to automate.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* The point */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-867">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-868">
          <motion.div variants={fadeIn} className="border-l-2 border-seaglass pl-8 py-2" data-id="element-869">
            <p className="eyebrow mb-3 text-seaglass" data-id="element-870">The point</p>
            <p className="text-body text-text mb-3" data-id="element-871">
              If AI is not helping yet, the problem may not be AI.
            </p>
            <p className="text-body text-text mb-3" data-id="element-872">
              It may be that the workflow, ownership, definitions, or business
              logic are still too unclear to support it.
            </p>
            <p className="text-body text-navy font-medium mb-3" data-id="element-873">
              AI can be a real lever.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-874">
              But only when it is attached to something the business can already
              trust.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-875">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-876">
          <BlueprintGrid data-id="element-877" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-878">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-879">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-880">
              Fix the recurring failure first.
            </span>{' '}
            <span className="text-white" data-id="element-881">Then decide what to automate.</span>
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-882">
            If AI is not helping yet, the problem may not be AI.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-883">
            <span data-id="element-884">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-885" />
          </Link>
          <p className="text-sm text-white/55 mt-4" data-id="element-886">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}